/**
 * Parse JSON from LLM text responses.
 *
 * LLMs often emit almost-JSON: markdown fences, trailing commas, smart quotes as
 * delimiters, or unquoted string values (e.g. "text": 「中文」). UTF-8 content
 * inside properly quoted strings is always preserved.
 */

function stripMarkdownFence(text) {
    const trimmed = text.trim();
    const fenceMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
    return fenceMatch ? fenceMatch[1].trim() : trimmed;
}

function extractJsonObject(text) {
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start === -1 || end <= start) {
        throw new Error('No JSON object in AI response');
    }
    return text.slice(start, end + 1);
}

function removeTrailingCommas(text) {
    return text.replace(/,\s*([}\]])/g, '$1');
}

/** Normalize curly/smart double quotes that LLMs use as JSON delimiters (not content). */
function normalizeStructuralQuotes(text) {
    return text.replace(/[\u201c\u201d\u201e\u2033]/g, '"');
}

function isJsonLiteralStart(text, index) {
    if (text.startsWith('true', index) || text.startsWith('false', index) || text.startsWith('null', index)) {
        return true;
    }
    const ch = text[index];
    return ch === '"' || ch === '{' || ch === '[' || ch === '-' || /[0-9]/.test(ch);
}

function escapeJsonString(value) {
    return value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\r/g, '\\r')
        .replace(/\n/g, '\\n')
        .replace(/\t/g, '\\t');
}

/**
 * Scan an unquoted scalar value until the next structural delimiter at depth 0.
 * Unlike the previous implementation, does not abort on embedded `"` characters.
 */
function scanUnquotedValueEnd(text, start) {
    let i = start;
    let depth = 0;

    while (i < text.length) {
        const ch = text[i];

        if (ch === '{' || ch === '[') {
            depth += 1;
        } else if (ch === '}' || ch === ']') {
            if (depth === 0) {
                return i;
            }
            depth -= 1;
        } else if (ch === ',' && depth === 0) {
            return i;
        }

        i += 1;
    }

    return i;
}

/**
 * Wrap unquoted string values in double quotes while respecting JSON string context.
 * Fixes: "value": 「設定「界限與時間」」
 */
function repairUnquotedJsonValues(text) {
    let result = '';
    let i = 0;
    let inString = false;
    let escape = false;

    while (i < text.length) {
        const ch = text[i];

        if (inString) {
            result += ch;
            if (escape) {
                escape = false;
            } else if (ch === '\\') {
                escape = true;
            } else if (ch === '"') {
                inString = false;
            }
            i += 1;
            continue;
        }

        if (ch === '"') {
            inString = true;
            result += ch;
            i += 1;
            continue;
        }

        if (ch === ':') {
            result += ch;
            i += 1;

            while (i < text.length && /\s/.test(text[i])) {
                result += text[i];
                i += 1;
            }

            if (i >= text.length || isJsonLiteralStart(text, i)) {
                continue;
            }

            const valueStart = i;
            i = scanUnquotedValueEnd(text, valueStart);
            const rawValue = text.slice(valueStart, i).trim();
            if (rawValue) {
                result += `"${escapeJsonString(rawValue)}"`;
            }
            continue;
        }

        result += ch;
        i += 1;
    }

    return result;
}

export function repairLlmJsonText(rawText) {
    let text = stripMarkdownFence(rawText);
    text = extractJsonObject(text);
    text = removeTrailingCommas(text);
    text = normalizeStructuralQuotes(text);
    text = repairUnquotedJsonValues(text);
    text = removeTrailingCommas(text);
    return text;
}

export function parseLlmJson(rawText) {
    if (!rawText || typeof rawText !== 'string') {
        throw new Error('Empty AI response');
    }

    const attempts = [
        () => JSON.parse(stripMarkdownFence(rawText.trim())),
        () => JSON.parse(repairLlmJsonText(rawText)),
    ];

    let lastError = null;
    for (const attempt of attempts) {
        try {
            return attempt();
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError || new Error('Could not parse AI JSON response');
}
